import {
  Fragment,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Twemoji from "react-twemoji";
import { ScopeType } from "sockapi/scope-types";
import {
  PublicSubmissionSummary,
  PublicSubmissionType,
} from "sockapi/scope-specs/public-submissions-spec";
import { PuzzleContentType } from "sockapi/scope-specs/puzzle-content-spec";
import { normalizeAnswer } from "sockapi/answers";
import { UNTITLED_PUZZLE_DISPLAY_NAME } from "sockapi/puzzle-data";
import { clsObj } from "client-util/util";
import { useGlobalScopeStatePosthuntable } from "stores/ScopeStateStore";
import { useNavStore } from "stores/NavStore";
import { Spinner } from "components/Loading";
import DownloadFailed from "components/DownloadFailed";
import TeamDisplayName from "components/TeamDisplayName";
import PuzzleContentDisplay from "components/PuzzleContent/PuzzleContentDisplay";
import { PuzzleLink, Link } from "components/Link";
import {
  PuzzleGallery,
  PuzzleImage,
  Table,
} from "components/PuzzleContent/puzzle-components";
import VolumeControl from "components/VolumeControl";
import { RouteName, useRouteMatch } from "components/urls";
import WrapupPageContent from "pages/wrapup/WrapupPageContent.mdx";
import WrapupShoutoutsContent from "pages/wrapup/WrapupShoutoutsContent.mdx";
import WrapupAmaContent from "pages/wrapup/WrapupAmaContent.mdx";
import { SFX, SFXPlayer } from "client-util/useAudioEffect";
import "./WrapupPage.css";

function WrapupNavLink({
  route,
  children,
}: {
  route: RouteName & `wrapup${string}`;
  children: ReactNode;
}) {
  const inPath = useRouteMatch(route);
  return inPath ? (
    <b style={{ color: "var(--link-text-color)" }}>{children}</b>
  ) : (
    <Link route={route}>{children}</Link>
  );
}

export function WrapupWrapper({ children }: { children: ReactNode }) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [heads, setHeads] = useState<Element[]>([]);

  useEffect(() => {
    if (heads.length > 0 || !contentRef.current) return;
    setHeads([
      ...contentRef.current.querySelectorAll(
        "h1,h2,h3:not(.puzzle-header-elem),h4,h5"
      ),
    ]);
  }, [heads.length]);

  useEffect(() => {
    const links = document.querySelectorAll(".wrapup-page aside div");
    const highlight = () => {
      const rects = heads.map((head) => head.getBoundingClientRect());
      heads.forEach((_, i) =>
        links[i].classList.toggle(
          "highlight",
          (!rects[i + 1] || rects[i + 1].top > 0) && rects[i].top < innerHeight
        )
      );
    };
    document.addEventListener("scroll", highlight, { passive: true });
    highlight();
    return () => document.removeEventListener("scroll", highlight);
  }, [heads]);

  return (
    <>
      <div className="fabric" />
      <div className="fabric-over" />
      <div className="centered-page wrapup-page">
        <aside>
          {heads.map((head, i) => (
            <div
              key={i}
              style={{ paddingLeft: `${head.tagName.slice(1)}ch` }}
              onClick={() => head.scrollIntoView()}
            >
              {head.textContent}
            </div>
          ))}
        </aside>
        <div className="wrapup-page-links">
          <WrapupNavLink route="wrapup">Wrap-Up</WrapupNavLink>
          <Twemoji>🧩</Twemoji>
          <WrapupNavLink route="wrapup_shoutouts">Team Shoutouts</WrapupNavLink>
          <WrapupNavLink route="wrapup_ama">Ask Us Anything</WrapupNavLink>
        </div>
        <div className="wrapup-page-content" ref={contentRef}>
          {children}
        </div>
      </div>
    </>
  );
}

const SUBMISSIONS_CAROUSEL_FILTERS: {
  kind: "free" | "tree";
  type: PublicSubmissionType;
  text: string;
}[] = [
  {
    kind: "free",
    type: PublicSubmissionType.PUBLIC_CUSTOM_PUZZLE,
    text: "Wishable Puzzles",
  },
  {
    kind: "free",
    type: PublicSubmissionType.PRIVATE_CUSTOM_PUZZLE,
    text: "Non-Wishable Puzzles",
  },
];

function CustomPuzzleSubmissionSlide(props: {
  teamId: string;
  displayName: string;
}) {
  const { teamId, displayName } = props;
  const [message, setMessage] = useState("");

  const puzName = `FIXME_${teamId}`;

  // Static answer checker for non-wishable custom puzzles.
  // This only works for puzzles with only one answer possibility,
  // which should be true for all non-wishable custom puzzles.
  const submit = (
    event:
      | React.FormEvent<HTMLFormElement>
      | React.MouseEvent<HTMLAnchorElement>
  ) => {
    event.preventDefault();

    const formData = new FormData(event.target as HTMLFormElement);
    const answer = formData.get("answer") as string | null;
    if (!answer) return;

    const normalized = normalizeAnswer(answer);
    const correctAnswer = normalizeAnswer("answer");

    setMessage(`${normalized}: ${normalized === correctAnswer ? "✅" : "❌"}`);
  };

  return (
    <div className="custom-puzzle-slide">
      <div className="puzzle-header">
        <h3 className="puzzle-header-elem">
          <Twemoji tag="span">{displayName}</Twemoji>
        </h3>
      </div>
      <PuzzleContentDisplay puzName={puzName} />
      <div className="note">
        This puzzle is not available via wishing. You can check your answer
        here:
        <form className="submit-answer-form" onSubmit={submit}>
          <div className="input-group">
            <input
              type="text"
              name="answer"
              autoComplete="off"
              required={true}
            />
            <a className="delete-row" href="#" onClick={submit}>
              →
            </a>
          </div>
        </form>
        {message && <p>{message}</p>}
        <details>
          <summary>View solution</summary>
          <div className="solution-answer">
            <b>Answer:</b>{" "}
            <div className="solved-title-answer spoiler">answer</div>
          </div>
          <PuzzleContentDisplay
            puzName={puzName}
            contentType={PuzzleContentType.SOLUTION}
          />
        </details>
      </div>
    </div>
  );
}

function SubmissionsCarousel({ kind }: { kind: "free" | "tree" }) {
  const publicSubmissionsStateResult = useGlobalScopeStatePosthuntable(
    ScopeType.PUBLIC_SUBMISSIONS,
    true,
    { hasPosthunt: true }
  );
  const teamListResult = useGlobalScopeStatePosthuntable(
    ScopeType.TEAM_LIST,
    true,
    { hasPosthunt: true }
  );
  const wrapupCustomPuzzleRedirectCommand = useNavStore(
    (store) => store.wrapupCustomPuzzleRedirectCommand
  );
  const setWrapupCustomPuzzleRedirectCommand = useNavStore(
    (store) => store.setWrapupCustomPuzzleRedirectCommand
  );
  const carouselRef = useRef<HTMLDivElement>(null);

  const defaultTypeFilter = useMemo(
    () => ({
      [PublicSubmissionType.PUBLIC_CUSTOM_PUZZLE]: kind === "free",
      [PublicSubmissionType.PRIVATE_CUSTOM_PUZZLE]: kind === "free",
    }),
    [kind]
  );
  const [typeFilter, setTypeFilter] = useState(defaultTypeFilter);
  const [teamNameFilter, setTeamNameFilter] = useState("");
  const [displayNameFilter, setDisplayNameFilter] = useState("");
  const [selectedSubmissionId, setSelectedSubmissionId] = useState("");

  const isDisplayNameFilterEnabled = kind === "free";

  const taskFilter = useCallback(
    (taskSummary: PublicSubmissionSummary) => {
      const { teamId, displayName, type } = taskSummary;
      if (teamListResult === null) return false;
      if (!teamListResult.success) return false;
      return (
        teamListResult.state[teamId].displayName
          .toLowerCase()
          .trim()
          .includes(teamNameFilter.toLowerCase().trim()) &&
        (!isDisplayNameFilterEnabled ||
          (displayName ?? UNTITLED_PUZZLE_DISPLAY_NAME)
            .toLowerCase()
            .trim()
            .includes(displayNameFilter.toLowerCase().trim())) &&
        (typeFilter[type] ?? true)
      );
    },
    [
      teamListResult,
      teamNameFilter,
      displayNameFilter,
      typeFilter,
      isDisplayNameFilterEnabled,
    ]
  );

  const submissions = useMemo(() => {
    if (!publicSubmissionsStateResult?.success) return [];
    const typeOrder = {
      [PublicSubmissionType.PUBLIC_CUSTOM_PUZZLE]: 0,
      [PublicSubmissionType.PRIVATE_CUSTOM_PUZZLE]: 1,
    };
    return Object.entries(publicSubmissionsStateResult.state.submissions)
      .filter(([submissionId, submission]) => taskFilter(submission))
      .sort(([submissionId1, submission1], [submissionId2, submission2]) => {
        const typeOrder1 = typeOrder[submission1.type];
        const typeOrder2 = typeOrder[submission2.type];
        const diffTypeOrder = typeOrder1 - typeOrder2;
        if (diffTypeOrder !== 0) return diffTypeOrder;
        return submission1.respondTime - submission2.respondTime;
      });
  }, [publicSubmissionsStateResult, taskFilter]);

  const numPerType = useMemo(() => {
    const numPerType: {
      [type in PublicSubmissionType]?: number;
    } = {};
    for (const [_, taskSummary] of submissions) {
      numPerType[taskSummary.type] = (numPerType[taskSummary.type] ?? 0) + 1;
    }
    return numPerType;
  }, [submissions]);

  useEffect(() => {
    if (kind !== "free") return;
    if (carouselRef.current === null) return;
    if (wrapupCustomPuzzleRedirectCommand === null) return;
    setTypeFilter(defaultTypeFilter);
    setTeamNameFilter("");
    setDisplayNameFilter("");
    setSelectedSubmissionId(wrapupCustomPuzzleRedirectCommand);
    carouselRef.current.scrollIntoView();
    setWrapupCustomPuzzleRedirectCommand(null);
  }, [
    wrapupCustomPuzzleRedirectCommand,
    setWrapupCustomPuzzleRedirectCommand,
    kind,
    defaultTypeFilter,
  ]);

  if (teamListResult === null) return <Spinner inline />;
  if (!teamListResult.success) return <DownloadFailed {...teamListResult} />;
  if (publicSubmissionsStateResult === null) return <Spinner inline />;
  if (!publicSubmissionsStateResult.success)
    return <DownloadFailed {...publicSubmissionsStateResult} />;

  const selectedSubmissionIndex = (() => {
    const index = submissions.findIndex(
      ([submissionId]) => submissionId === selectedSubmissionId
    );
    if (index === -1) return 0;
    return index;
  })();
  const selectedSubmissionEntry = submissions[selectedSubmissionIndex];

  return (
    <div className="wrapup-carousel" ref={carouselRef}>
      <div className="admin-queue-header">
        <div className="admin-queue-header-actions">
          <div className="admin-queue-team-name-filter">
            <Twemoji tag="span">🔍</Twemoji>{" "}
            <input
              type="text"
              placeholder="Filter by team name"
              value={teamNameFilter}
              onChange={(e) => {
                setTeamNameFilter(e.target.value);
              }}
            />
          </div>
          {isDisplayNameFilterEnabled ? (
            <div className="admin-queue-team-name-filter">
              <Twemoji tag="span">🔍</Twemoji>{" "}
              <input
                type="text"
                placeholder="Filter by title"
                value={displayNameFilter}
                onChange={(e) => {
                  setDisplayNameFilter(e.target.value);
                }}
              />
            </div>
          ) : null}
        </div>
      </div>
      <div className="admin-queue-header admin-queue-type-filter">
        {SUBMISSIONS_CAROUSEL_FILTERS.filter(
          (filter) => filter.kind === kind
        ).map(({ type, text }) => (
          <button
            key={type}
            className={clsObj({ active: typeFilter[type] ?? true })}
            onClick={() => {
              setTypeFilter({
                ...typeFilter,
                [type]: !(typeFilter[type] ?? true),
              });
            }}
          >
            {text}{" "}
            {(typeFilter[type] ?? true) ? <>({numPerType[type] ?? 0})</> : null}
          </button>
        ))}
      </div>
      {selectedSubmissionEntry !== undefined ? (
        <div className="wrapup-carousel-selector">
          <div
            className="control-prev control-arrow"
            onClick={() => {
              const newIndex = Math.max(selectedSubmissionIndex - 1, 0);
              setSelectedSubmissionId(submissions[newIndex][0]);
            }}
          />
          <ul className="control-dots">
            {submissions.map(([submissionId, _]) => (
              <li
                key={submissionId}
                className={clsObj({
                  dot: true,
                  selected:
                    selectedSubmissionEntry !== undefined &&
                    selectedSubmissionEntry[0] === submissionId,
                })}
                onClick={() => {
                  setSelectedSubmissionId(submissionId);
                }}
              ></li>
            ))}
          </ul>
          <div
            className="control-next control-arrow"
            onClick={() => {
              const newIndex = Math.min(
                selectedSubmissionIndex + 1,
                submissions.length - 1
              );
              setSelectedSubmissionId(submissions[newIndex][0]);
            }}
          />
        </div>
      ) : null}
      <div className="admin-queue-list">
        {selectedSubmissionEntry !== undefined ? (
          <>
            {(([_, submission]) => {
              switch (submission.type) {
                case PublicSubmissionType.PUBLIC_CUSTOM_PUZZLE:
                case PublicSubmissionType.PRIVATE_CUSTOM_PUZZLE: {
                  return (
                    <CustomPuzzleSubmissionSlide
                      key={submission.teamId}
                      teamId={submission.teamId}
                      displayName={
                        submission.displayName ?? UNTITLED_PUZZLE_DISPLAY_NAME
                      }
                    />
                  );
                }
              }
            })(selectedSubmissionEntry)}
          </>
        ) : null}
      </div>
    </div>
  );
}

function WrapupPrivateCustomPuzzleLink({ teamId }: { teamId: string }) {
  const publicSubmissionsStateResult = useGlobalScopeStatePosthuntable(
    ScopeType.PUBLIC_SUBMISSIONS,
    true,
    { hasPosthunt: true }
  );
  const setWrapupCustomPuzzleRedirectCommand = useNavStore(
    (store) => store.setWrapupCustomPuzzleRedirectCommand
  );

  if (publicSubmissionsStateResult === null) return <Spinner inline />;
  if (!publicSubmissionsStateResult.success)
    return <DownloadFailed {...publicSubmissionsStateResult} />;

  const submissionEntry = Object.entries(
    publicSubmissionsStateResult.state.submissions
  ).find(
    ([submissionId, submission]) =>
      submission.type === PublicSubmissionType.PRIVATE_CUSTOM_PUZZLE &&
      submission.teamId === teamId
  );

  if (submissionEntry === undefined) {
    console.error(`could not find custom puzzle for team ${teamId}`);
    return (
      <a
        href="#"
        onClick={(event) => {
          event.preventDefault();
        }}
      >
        ???
      </a>
    );
  }

  const [submissionId, { displayName = UNTITLED_PUZZLE_DISPLAY_NAME }] =
    submissionEntry;

  return (
    <a
      href="#"
      onClick={(event) => {
        event.preventDefault();
        setWrapupCustomPuzzleRedirectCommand(submissionId);
      }}
    >
      {displayName}
    </a>
  );
}

function Credits({ credits }: { credits: string }) {
  const lines = credits
    .trim()
    .split("\n")
    .map((line, i) => {
      if (line === "") {
        return <div key={i} className="credits-break" />;
      }

      if (!line.includes(":")) {
        return (
          <div key={i} className="credits-header">
            {line}
          </div>
        );
      }

      const [title, people] = line.split(": ");
      const peopleList = people
        .split(", ")
        .map((person, i) => <li key={i}>{person}</li>);

      const cols = (() => {
        switch (peopleList.length) {
          case 1:
            return 1;
          case 2:
          case 4:
            return 2;
          default:
            return 3;
        }
      })();
      const remainder = peopleList.length % cols;

      return (
        <div key={i} className="credit">
          <div className="credit-title">{title}</div>
          <ul className={`credit-people col-${cols} remainder-${remainder}`}>
            {peopleList}
          </ul>
        </div>
      );
    });

  return <div className="credits">{lines}</div>;
}

export default function WrapupPage() {
  return (
    <WrapupWrapper>
      <WrapupPageContent
        components={{
          Puzzle: PuzzleLink,
          Gallery: PuzzleGallery,
          Fragment: Fragment,
          Twemoji,
          Link,
          Credits,
          Image: (props) => (
            <PuzzleImage staticPuzDir="" unscoped center {...props} />
          ),
        }}
      />
    </WrapupWrapper>
  );
}

export function WrapupShoutoutsPage() {
  return (
    <WrapupWrapper>
      <WrapupShoutoutsContent
        components={{
          Puzzle: PuzzleLink,
          PrivateCustomPuzzle: WrapupPrivateCustomPuzzleLink,
          Team: TeamDisplayName,
          Gallery: PuzzleGallery,
          Fragment: Fragment,
          Carousel: SubmissionsCarousel,
          Image: (props) => (
            <PuzzleImage staticPuzDir="" unscoped center {...props} />
          ),
          Twemoji,
        }}
      />
    </WrapupWrapper>
  );
}

const MAPPED_SFX_NAME = [
  ["NOTIF-WAVE", "Admin queue"],
  ["NOTIF-BOSSA-A", "Hint answered"],
  ["NOTIF-BOSSA-B", "Unlock"],
  ["ROCK-FLOPSTER", "Erratum"],
  ["NOTIF-CLAPS", "Solve"],
  ["NOTIF-CHEER", "Victory (cheer)"],
  ["NOTIF-BOSSA-AB", "Victory (music)"],
] as [SFX, string][];

function SFXTester() {
  return (
    <div>
      <div className="sfx-tester">
        {MAPPED_SFX_NAME.map(([sfx, name]) => (
          <button key={name} onClick={() => SFXPlayer.play(SFX[sfx])}>
            {name}
          </button>
        ))}
      </div>
      <div className="sfx-tester-volume-control">
        <VolumeControl />
      </div>
    </div>
  );
}

const SPEAKERS = {
  "Big Branch": "big_branch.png",
  "Funny Farmer": "farmer.png",
  Kero: "kero-tight.png",
  Kid: "kid.png",
  "Mop Mop": "mop_mop.png",
  Olive: "olive.png",
  "Pom Pom": "pom_pom.png",
  "Rock Lobster": "rock_lobster.png",
  Stelle: "stelle-tight.png",
  "Ten Lions": "lions.png",
} as const;

function Speaker({
  name,
  children,
}: {
  name: keyof typeof SPEAKERS;
  children: ReactNode;
}) {
  const image = SPEAKERS[name];
  return (
    <div className="speaker">
      <div className="speaker-image">
        <img alt="" src={`/static/images/cutscenes/${image}`} />
      </div>
      <div className="speaker-content">
        <b>{name}: </b>
        <div>{children}</div>
      </div>
    </div>
  );
}

export function WrapupAmaPage() {
  return (
    <WrapupWrapper>
      <WrapupAmaContent
        components={{
          Link,
          SFXTester,
          Twemoji,
          Speaker,
          Image: (props) => (
            <PuzzleImage staticPuzDir="" unscoped center {...props} />
          ),
          Table,
        }}
      />
    </WrapupWrapper>
  );
}
